import {createSelector} from 'reselect';
import {emptyArray, entries} from '@packages/utilities/fp/utils';
import type {DesignerStores} from '@packages/systems/designer/flux';
import type {
  EaseConfig,
  Interaction,
  Timeline,
  TimelineId,
} from '@packages/systems/ix3/core';
import {TimelineControlType} from '@packages/systems/ix3/core';
import {weakMemo} from '@packages/utilities/memo';

export type GsapScriptUrl =
  `https://cdn.prod.website-files.com/gsap/3.15.0/${string}.min.js`; //change this when we start versioning automatically

type PopularGsapPluginKey = 'Flip' | 'ScrollTrigger' | 'SplitText';

type AdvancedGsapPluginKey =
  | 'Draggable'
  | 'DrawSVGPlugin'
  | 'InertiaPlugin'
  | 'MorphSVGPlugin'
  | 'MotionPathPlugin'
  | 'MotionPathHelper'
  | 'Observer'
  | 'Physics2DPlugin'
  | 'PhysicsPropsPlugin'
  | 'ScrambleTextPlugin'
  | 'ScrollSmoother'
  | 'ScrollToPlugin'
  | 'TextPlugin';

type EaseGsapPluginKey =
  | 'CustomBounce'
  | 'CustomEase'
  | 'CustomWiggle'
  | 'EasePack';

export type GsapPluginKey =
  | PopularGsapPluginKey
  | AdvancedGsapPluginKey
  | EaseGsapPluginKey
  | 'core';

export type GsapPluginType = {
  // id includes the FULL NAME of the plugin, this is what the URL/file name is based on
  id: GsapPluginKey;
  // name represents the display name in the UI
  name: string;
  description: string;
  cdnUrl: GsapScriptUrl;
  requires?: GsapPluginKey[];
};

export type GsapIntegrationSettings = {
  isGsapEnabled: boolean;
  gsapPluginsEnabled: GsapPluginKey[];
};

export const GSAP_CORE_CDN_URL: GsapScriptUrl =
  'https://cdn.prod.website-files.com/gsap/3.15.0/gsap.min.js';

export const GSAP_PLUGINS: {
  popular: {[K in PopularGsapPluginKey]: GsapPluginType & {id: K}};
  advanced: {[K in AdvancedGsapPluginKey]: GsapPluginType & {id: K}};
  eases: {[K in EaseGsapPluginKey]: GsapPluginType & {id: K}};
} = {
  popular: {
    Flip: {
      id: 'Flip',
      name: 'Flip',
      description: 'Smooth layout transitions',
      cdnUrl: 'https://cdn.prod.website-files.com/gsap/3.15.0/Flip.min.js',
    },
    ScrollTrigger: {
      id: 'ScrollTrigger',
      name: 'ScrollTrigger',
      description: 'Scroll-based animations',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/ScrollTrigger.min.js',
    },
    SplitText: {
      id: 'SplitText',
      name: 'SplitText',
      description: 'Split text into characters, words, and/or lines',
      cdnUrl: 'https://cdn.prod.website-files.com/gsap/3.15.0/SplitText.min.js',
    },
  },
  advanced: {
    Draggable: {
      id: 'Draggable',
      name: 'Draggable',
      description: 'Make elements draggable',
      cdnUrl: 'https://cdn.prod.website-files.com/gsap/3.15.0/Draggable.min.js',
    },
    DrawSVGPlugin: {
      id: 'DrawSVGPlugin',
      name: 'DrawSVG',
      description: 'Animate SVG strokes',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/DrawSVGPlugin.min.js',
    },
    InertiaPlugin: {
      id: 'InertiaPlugin',
      name: 'Inertia',
      description: 'Smooth movement with momentum',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/InertiaPlugin.min.js',
    },
    MorphSVGPlugin: {
      id: 'MorphSVGPlugin',
      name: 'MorphSVG',
      description: 'Morph between SVG shapes',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/MorphSVGPlugin.min.js',
    },
    MotionPathPlugin: {
      id: 'MotionPathPlugin',
      name: 'MotionPath',
      description: 'Animate along complex paths',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/MotionPathPlugin.min.js',
    },
    MotionPathHelper: {
      id: 'MotionPathHelper',
      name: 'MotionPathHelper',
      description: 'Visual path editing',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/MotionPathHelper.min.js',
      requires: ['MotionPathPlugin'],
    },
    Observer: {
      id: 'Observer',
      name: 'Observer',
      description: 'React to user events (scroll, pointer, touch, wheel)',
      cdnUrl: 'https://cdn.prod.website-files.com/gsap/3.15.0/Observer.min.js',
    },
    Physics2DPlugin: {
      id: 'Physics2DPlugin',
      name: 'Physics2D',
      description: 'Simple physics simulation for x/y movement',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/Physics2DPlugin.min.js',
    },
    PhysicsPropsPlugin: {
      id: 'PhysicsPropsPlugin',
      name: 'PhysicsProps',
      description: 'Simple physics simulation for arbitrary properties',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/PhysicsPropsPlugin.min.js',
    },
    ScrambleTextPlugin: {
      id: 'ScrambleTextPlugin',
      name: 'ScrambleText',
      description: 'Randomized text transition effects',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/ScrambleTextPlugin.min.js',
    },
    ScrollSmoother: {
      id: 'ScrollSmoother',
      name: 'ScrollSmoother',
      description: 'Smooth scrolling experience',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/ScrollSmoother.min.js',
      requires: ['ScrollTrigger'],
    },
    ScrollToPlugin: {
      id: 'ScrollToPlugin',
      name: 'ScrollTo',
      description: 'Animate scrolling to positions',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/ScrollToPlugin.min.js',
    },
    TextPlugin: {
      id: 'TextPlugin',
      name: 'Text',
      description: 'Animate text content',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/TextPlugin.min.js',
    },
  },
  eases: {
    CustomEase: {
      id: 'CustomEase',
      name: 'CustomEase',
      description: 'Create any easing curve imaginable',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/CustomEase.min.js',
    },
    CustomBounce: {
      id: 'CustomBounce',
      name: 'CustomBounce',
      description: 'Set the bounce, squash, and stretch. Requires CustomEase',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/CustomBounce.min.js',
      requires: ['CustomEase'],
    },
    CustomWiggle: {
      id: 'CustomWiggle',
      name: 'CustomWiggle',
      description: 'Set a wiggle amount and type. Requires CustomEase',
      cdnUrl:
        'https://cdn.prod.website-files.com/gsap/3.15.0/CustomWiggle.min.js',
      requires: ['CustomEase'],
    },
    EasePack: {
      id: 'EasePack',
      name: 'EasePack',
      description: 'Rough, expoScale, and slow eases',
      cdnUrl: 'https://cdn.prod.website-files.com/gsap/3.15.0/EasePack.min.js',
    },
  },
} as const;

export const ALL_GSAP_PLUGINS = Object.values(GSAP_PLUGINS).reduce(
  (acc, category) => {
    for (const [key, plugin] of entries(
      category as Record<GsapPluginKey, GsapPluginType>
    )) {
      acc.set(key, plugin);
    }
    return acc;
  },
  new Map<GsapPluginKey, GsapPluginType>()
);

export const OLD_TO_NEW_PLUGIN_NAME_MAP = {
  DrawSVG: 'DrawSVGPlugin',
  Inertia: 'InertiaPlugin',
  MorphSVG: 'MorphSVGPlugin',
  MotionPath: 'MotionPathPlugin',
  Physics2D: 'Physics2DPlugin',
  PhysicsProps: 'PhysicsPropsPlugin',
  ScrambleText: 'ScrambleTextPlugin',
  ScrollTo: 'ScrollToPlugin',
  Text: 'TextPlugin',
} as const;

// adding migration logic so that the id attribute always matches the key, which should be same as the file name
export function migrateGsapPlugins(plugins: string[]): GsapPluginKey[] {
  return plugins.map(
    (pluginId: string) =>
      OLD_TO_NEW_PLUGIN_NAME_MAP[
        pluginId as keyof typeof OLD_TO_NEW_PLUGIN_NAME_MAP
      ] || pluginId
  );
}

function addPluginDependenciesForKeys(
  pluginKeys: Iterable<GsapPluginKey>
): GsapPluginKey[] {
  const addedPlugins = new Set<GsapPluginKey>();

  // First pass: collect all plugins and their dependencies
  for (const pluginKey of pluginKeys) {
    addedPlugins.add(pluginKey);

    const requiredPlugins = ALL_GSAP_PLUGINS.get(pluginKey)?.requires;

    // Add required dependencies if any exist
    if (requiredPlugins) {
      for (const requiredPlugin of requiredPlugins) {
        addedPlugins.add(requiredPlugin);
      }
    }
  }

  // Convert to array and sort so dependencies come first
  const sortedPlugins = Array.from(addedPlugins).sort((a, b) => {
    // If a requires b, b should come first
    if (ALL_GSAP_PLUGINS.get(a)?.requires?.includes(b)) return 1;
    if (ALL_GSAP_PLUGINS.get(b)?.requires?.includes(a)) return -1;
    // Otherwise maintain stable sort
    return 0;
  });

  return sortedPlugins;
}

/*
 * Ensures that if plugin A requires plugin B, plugin B will be injected first
 */
function addPluginDependencies({
  gsapPluginsEnabled,
}: GsapIntegrationSettings): GsapPluginKey[] {
  return addPluginDependenciesForKeys(gsapPluginsEnabled);
}

const EASE_TO_PLUGIN: Partial<
  Record<Exclude<EaseConfig, number>['type'], GsapPluginKey | undefined>
> = {
  customEase: GSAP_PLUGINS.eases.CustomEase.id,
  customBounce: GSAP_PLUGINS.eases.CustomBounce.id,
  customWiggle: GSAP_PLUGINS.eases.CustomWiggle.id,
  rough: GSAP_PLUGINS.eases.EasePack.id,
  slowMo: GSAP_PLUGINS.eases.EasePack.id,
  expoScale: GSAP_PLUGINS.eases.EasePack.id,
};

function addEasePluginKey(
  ease: EaseConfig | undefined,
  target: Set<GsapPluginKey>
): void {
  if (!ease || typeof ease === 'number') return;
  const pluginKey = EASE_TO_PLUGIN[ease.type];
  if (pluginKey) target.add(pluginKey);
}

export type GsapScript = {pluginKey: GsapPluginKey; url: GsapScriptUrl};

const GSAP_CORE_CDN_OBJ: GsapScript = {
  pluginKey: 'core',
  url: GSAP_CORE_CDN_URL,
};

/*
 * Converts plugin array to array of CDN URLs
 */
const _getGsapScripts = weakMemo((settings: GsapIntegrationSettings) =>
  weakMemo((ix3Interactions: Interaction[]) =>
    weakMemo((ix3Timelines: Timeline[]): Array<GsapScript> => {
      const finalScripts = new Map<GsapPluginKey, GsapScript>();
      const hasIx3Interactions = ix3Interactions.length > 0;

      if (settings.isGsapEnabled) {
        finalScripts.set('core', GSAP_CORE_CDN_OBJ);
        for (const pluginKey of addPluginDependencies(settings)) {
          const cdnUrl = ALL_GSAP_PLUGINS.get(pluginKey)?.cdnUrl;
          if (cdnUrl) finalScripts.set(pluginKey, {pluginKey, url: cdnUrl});
        }
      }

      if (!hasIx3Interactions) {
        return Array.from(finalScripts, ([, script]) => script);
      }

      const timelinesMap = ix3Timelines.reduce(
        (acc, timeline) => {
          acc[timeline.id] = {
            actions: timeline.actions,
            settings: timeline.settings,
          };
          return acc;
        },
        {} as Record<
          TimelineId,
          {actions: Timeline['actions']; settings?: Timeline['settings']}
        >
      );

      const easePlugins = new Set<GsapPluginKey>();

      for (const {timelineIds, triggers} of ix3Interactions) {
        for (const timelineId of timelineIds) {
          const timelineData = timelinesMap[timelineId];
          const timelineActions = timelineData?.actions;
          if (timelineActions?.length) {
            finalScripts.set('core', GSAP_CORE_CDN_OBJ);
            if (timelineData?.settings) {
              addEasePluginKey(timelineData.settings.ease, easePlugins);
              // Stagger easing is basic today, but we detect plugins for future use.
              addEasePluginKey(
                timelineData.settings.stagger?.ease,
                easePlugins
              );
            }
            for (const timelineAction of timelineActions) {
              if (timelineAction.splitText) {
                finalScripts.set(GSAP_PLUGINS.popular.SplitText.id, {
                  pluginKey: GSAP_PLUGINS.popular.SplitText.id,
                  url: GSAP_PLUGINS.popular.SplitText.cdnUrl,
                });
              }
              if (timelineAction.timing) {
                addEasePluginKey(timelineAction.timing.ease, easePlugins);
                // Stagger easing is basic today, but we detect plugins for future use.
                addEasePluginKey(
                  timelineAction.timing.stagger?.ease,
                  easePlugins
                );
              }
            }
          }
        }
        for (const [, config] of triggers) {
          if (config.controlType === TimelineControlType.SCROLL) {
            finalScripts.set(GSAP_PLUGINS.popular.ScrollTrigger.id, {
              pluginKey: GSAP_PLUGINS.popular.ScrollTrigger.id,
              url: GSAP_PLUGINS.popular.ScrollTrigger.cdnUrl,
            });
          }
          // CONTINUOUS triggers (mouse-move, Follow Mouse, etc.) need GSAP core
          // at runtime — Observer listens, quickSetter writes. Setting 'core'
          // here covers the case where isGsapEnabled is off and the interaction
          // has no timeline (e.g. Follow Mouse actions).
          if (config.controlType === TimelineControlType.CONTINUOUS) {
            finalScripts.set('core', GSAP_CORE_CDN_OBJ);
            finalScripts.set(GSAP_PLUGINS.advanced.Observer.id, {
              pluginKey: GSAP_PLUGINS.advanced.Observer.id,
              url: GSAP_PLUGINS.advanced.Observer.cdnUrl,
            });
          }
        }
      }
      if (easePlugins.size > 0) {
        for (const pluginKey of addPluginDependenciesForKeys(easePlugins)) {
          const cdnUrl = ALL_GSAP_PLUGINS.get(pluginKey)?.cdnUrl;
          if (cdnUrl) finalScripts.set(pluginKey, {pluginKey, url: cdnUrl});
        }
      }

      return Array.from(finalScripts, ([, script]) => script);
    })
  )
);

export const defaultGsapIntegrationSettings = {
  isGsapEnabled: false,
  gsapPluginsEnabled: [],
} as GsapIntegrationSettings;

export type CommaJoinedGsapScripts = string & {
  __brand: 'CommaJoinedGsapScripts';
};

export function parseCommaJoinedGsapScripts(
  joined: CommaJoinedGsapScripts
): GsapScriptUrl[] {
  return joined.split(',') as GsapScriptUrl[];
}

export function getGsapScriptsAsCommaJoinedString(
  settings: GsapIntegrationSettings = defaultGsapIntegrationSettings,
  interaction: Interaction[] = emptyArray,
  timelines: Timeline[] = emptyArray
): CommaJoinedGsapScripts {
  return _getGsapScripts(settings)(interaction)(timelines)
    .map((script) => script.url)
    .join(',') as CommaJoinedGsapScripts;
}

export function getGsapScripts(
  settings: GsapIntegrationSettings = defaultGsapIntegrationSettings,
  interaction: Interaction[] = emptyArray,
  timelines: Timeline[] = emptyArray
): Array<GsapScript> {
  return _getGsapScripts(settings)(interaction)(timelines);
}

/*
 * Certain plugins have "Plugin" at the end of their name, which we need when we register them
 */
export function generateGsapRegisterJs(gsapScripts: Array<GsapScript>): string {
  // this should never happen but just in case
  if (gsapScripts.length === 0) return '';
  const finalPluginsToRegister: string[] = [];
  for (const {pluginKey} of gsapScripts) {
    if (pluginKey !== 'core')
      finalPluginsToRegister.push(ALL_GSAP_PLUGINS.get(pluginKey)!.id);
  }
  return `gsap.registerPlugin(${finalPluginsToRegister.join(',')});`;
}

export const selectGsapIntegregationData = createSelector(
  (state: DesignerStores) =>
    state.SiteDataStore.gsapIntegrationSettings?.isGsapEnabled,
  (state: DesignerStores) =>
    state.SiteDataStore.gsapIntegrationSettings?.gsapPluginsEnabled,
  (isGsapEnabled, gsapPluginsEnabled) => ({
    isGsapEnabled,
    gsapPluginsEnabled,
  })
);

export const forTestSuite = {
  GSAP_CORE_CDN_URL,
};
